/**
 * Parses JSON from WordPress REST responses that may include PHP
 * deprecation/warning noise before or after the payload.
 */
export const parseWordpressJson = <T = unknown>(raw: string): T => {
  const text = raw.trim();
  if (!text) {
    throw new Error('Empty WordPress response');
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    // WordPress sometimes prepends/appends PHP warnings; extract the JSON payload.
  }

  const arrayStart = text.indexOf('[');
  const objectStart = text.indexOf('{');
  const candidates = [
    arrayStart === -1 ? null : { index: arrayStart, open: '[', close: ']' },
    objectStart === -1 ? null : { index: objectStart, open: '{', close: '}' },
  ].filter((candidate): candidate is { index: number; open: string; close: string } => candidate != null);

  if (candidates.length === 0) {
    throw new Error('No JSON payload found in WordPress response');
  }

  const start = candidates.reduce((earliest, current) =>
    current.index < earliest.index ? current : earliest
  );
  const extracted = extractBalancedJson(text, start.index, start.open, start.close);
  return JSON.parse(extracted) as T;
};

export const parseWordpressResponseJson = async <T = unknown>(response: Response): Promise<T> => {
  const text = await response.text();
  return parseWordpressJson<T>(text);
};

const extractBalancedJson = (text: string, start: number, open: string, close: string): string => {
  let depth = 0;
  let inString = false;
  let escape = false;

  for (let i = start; i < text.length; i += 1) {
    const ch = text[i];

    if (inString) {
      if (escape) {
        escape = false;
      } else if (ch === '\\') {
        escape = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }

    if (ch === open) {
      depth += 1;
    } else if (ch === close) {
      depth -= 1;
      if (depth === 0) {
        return text.slice(start, i + 1);
      }
    }
  }

  throw new Error('Unterminated JSON payload in WordPress response');
};
